import { ArrowLeftOutlined, EditOutlined, SoundOutlined } from '@ant-design/icons'
import { Alert, Button, Image, Skeleton, Tag } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { getVirtualIpAsset, type SaasApiError, type VirtualIpAssetDetail } from '../../api'
import { virtualIpAssetsPath, virtualIpProjectBase } from './virtualIpPaths'
import './virtualIp.css'

function asText(value: unknown): string {
  return typeof value === 'string' && value.trim() ? value.trim() : ''
}

function asList(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.map((v) => asText(v)).filter(Boolean)
}

export default function VirtualIpAssetDetailPage() {
  const { t } = useTranslation('virtualIp')
  const { workspaceId = '', assetId = '' } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [asset, setAsset] = useState<VirtualIpAssetDetail | null>(null)

  const load = useCallback(() => {
    if (!workspaceId || !assetId) return
    setLoading(true)
    setError(null)
    getVirtualIpAsset(workspaceId, assetId)
      .then((r) => setAsset(r.data))
      .catch((e) => setError((e as SaasApiError).message || t('ia.loadAssetFailed')))
      .finally(() => setLoading(false))
  }, [workspaceId, assetId, t])

  useEffect(() => {
    load()
  }, [load])

  if (loading) {
    return (
      <div className="tc-vip-page" data-testid="virtual-ip-asset-detail-loading">
        <Skeleton active avatar paragraph={{ rows: 8 }} />
      </div>
    )
  }

  if (error || !asset) {
    return (
      <div className="tc-vip-page">
        <Alert
          type="error"
          showIcon
          message={t('ia.loadAssetFailed')}
          description={error}
          action={
            <Button onClick={() => navigate(virtualIpAssetsPath(workspaceId))}>{t('ia.backToAssets')}</Button>
          }
        />
      </div>
    )
  }

  const profile = asset.positioning_profile || {}
  const platforms = asList(profile.platforms || profile.primary_platforms)
  const cover = asset.cover_url || asset.character?.cover_url || null

  return (
    <div className="tc-vip-page vip-ia-asset-detail" data-testid="virtual-ip-asset-detail">
      <div className="tc-vip-topbar">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(virtualIpAssetsPath(workspaceId))}
        >
          {t('ia.backToAssets')}
        </Button>
        <Button
          type="primary"
          icon={<EditOutlined />}
          data-testid="virtual-ip-asset-edit-project"
          onClick={() => navigate(virtualIpProjectBase(workspaceId, asset.project_id))}
        >
          {t('ia.editProject')}
        </Button>
      </div>

      <section className="vip-ia-hero">
        <div className="vip-ia-hero-visual">
          {cover ? <Image src={cover} alt={asset.name} className="vip-ia-hero-image" /> : (
            <div className="vip-ia-card-placeholder vip-ia-hero-placeholder">{t('ia.coverPlaceholder')}</div>
          )}
        </div>
        <div className="vip-ia-hero-copy">
          <Tag color="success">{t('ia.projectStatus.completed')}</Tag>
          <h1 data-i18n-exempt="business-data">{asset.name}</h1>
          {asset.one_liner ? <p className="vip-ia-hero-oneliner" data-i18n-exempt="business-data">{asset.one_liner}</p> : null}
          <div className="vip-ia-card-tags">
            {asset.tags.map((tag) => (
              <Tag key={tag}>{tag}</Tag>
            ))}
          </div>
        </div>
      </section>

      <section className="vip-ia-section">
        <h2>{t('ia.sections.identity')}</h2>
        <dl className="vip-ia-dl">
          <div>
            <dt>{t('ia.fields.ipType')}</dt>
            <dd data-i18n-exempt="business-data">
              {asText(profile.selected_ip_type_display_name) ||
                asText(profile.selected_ip_type) ||
                asText(asset.selected_ip_type) ||
                t('common.emDash')}
            </dd>
          </div>
          <div>
            <dt>{t('ia.fields.role')}</dt>
            <dd data-i18n-exempt="business-data">{asText(profile.role) || asText(profile.persona) || t('common.emDash')}</dd>
          </div>
          <div>
            <dt>{t('ia.fields.differentiation')}</dt>
            <dd data-i18n-exempt="business-data">
              {asText(profile.differentiation) || asText(profile.core_differentiation) || t('common.emDash')}
            </dd>
          </div>
        </dl>
      </section>

      <section className="vip-ia-section">
        <h2>{t('ia.sections.audience')}</h2>
        <dl className="vip-ia-dl">
          <div>
            <dt>{t('ia.fields.audience')}</dt>
            <dd data-i18n-exempt="business-data">
              {asText(profile.target_audience) || asText(profile.audience_summary) || t('common.emDash')}
            </dd>
          </div>
        </dl>
      </section>

      <section className="vip-ia-section">
        <h2>{t('ia.sections.content')}</h2>
        <dl className="vip-ia-dl">
          <div>
            <dt>{t('ia.fields.contentDirection')}</dt>
            <dd data-i18n-exempt="business-data">
              {asText(profile.content_direction) || asText(profile.content_style) || t('common.emDash')}
            </dd>
          </div>
          <div>
            <dt>{t('ia.fields.contentThemes')}</dt>
            <dd data-i18n-exempt="business-data">
              {asList(profile.content_themes).join(' · ') || asText(profile.content_topics) || t('common.emDash')}
            </dd>
          </div>
        </dl>
      </section>

      {platforms.length > 0 ? (
        <section className="vip-ia-section">
          <h2>{t('ia.sections.platforms')}</h2>
          <div className="vip-ia-card-tags">
            {platforms.map((p) => (
              <Tag key={p}>{p}</Tag>
            ))}
          </div>
        </section>
      ) : null}

      {asset.character?.cover_url || cover ? (
        <section className="vip-ia-section">
          <h2>{t('ia.sections.character')}</h2>
          <div className="vip-ia-character-block">
            <Image src={(asset.character?.cover_url || cover)!} width={220} alt={asset.name} />
            <p data-i18n-exempt="business-data">
              {asText(profile.appearance) || asText(profile.visual_style) || t('common.emDash')}
            </p>
          </div>
        </section>
      ) : null}

      {asset.voice ? (
        <section className="vip-ia-section">
          <h2>
            <SoundOutlined /> {t('ia.sections.voice')}
          </h2>
          <dl className="vip-ia-dl">
            <div>
              <dt>{t('ia.fields.voiceName')}</dt>
              <dd data-i18n-exempt="business-data">{asText(asset.voice.display_name) || t('common.emDash')}</dd>
            </div>
            <div>
              <dt>{t('ia.fields.voiceMode')}</dt>
              <dd data-i18n-exempt="business-data">{asText(asset.voice.voice_mode) || t('common.emDash')}</dd>
            </div>
            <div>
              <dt>{t('ia.fields.voiceDescription')}</dt>
              <dd data-i18n-exempt="business-data">{asText(asset.voice.description) || t('common.emDash')}</dd>
            </div>
          </dl>
        </section>
      ) : null}

      <section className="vip-ia-section">
        <h2>{t('ia.sections.summary')}</h2>
        <p className="vip-ia-summary" data-i18n-exempt="business-data">
          {asText(profile.summary) ||
            asText(profile.positioning_summary) ||
            asText(profile.strategy_summary) ||
            asset.one_liner ||
            t('common.emDash')}
        </p>
      </section>
    </div>
  )
}
